import React, { useState, useEffect, useCallback } from 'react';
import { Mail, AlertTriangle, Clock, ArrowRight, X, CheckCircle, Loader2 } from 'lucide-react';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import './EmailVerificationBanner.css';

const EmailVerificationBanner = () => {
  const { getAxios } = useSecureAuth();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const fetchVerificationStatus = useCallback(async () => {
    try {
      const axios = getAxios();
      const response = await axios.get('/merchant/auth/verification-status');

      if (response.data.success) {
        setVerificationStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    } finally {
      setLoading(false);
    }
  }, [getAxios]);

  useEffect(() => {
    fetchVerificationStatus();
  }, [fetchVerificationStatus]);

  const handleResendVerification = async () => {
    if (sending || sent) return;

    try {
      setSending(true);
      const axios = getAxios();
      await axios.post('/merchant/auth/resend-verification');
      setSent(true);

      // Reset sent state after 60 seconds
      setTimeout(() => setSent(false), 60000);
    } catch (error) {
      console.error('Error resending verification email:', error);
    } finally {
      setSending(false);
    }
  };

  // Don't show if loading, verified, or dismissed
  if (loading || !verificationStatus || verificationStatus.isEmailVerified || dismissed) {
    return null;
  }

  const { phase, hoursRemaining, email, isBlocked, isLimited } = verificationStatus;

  // Determine banner style and message based on phase
  const getBannerConfig = () => {
    switch (phase) {
      case 'BLOCKED':
        return {
          className: 'evb-blocked',
          icon: AlertTriangle,
          iconColor: '#dc2626',
          message: (
            <>
              <strong>Account restricted.</strong> Please verify your email to continue using the platform.
            </>
          ),
          urgent: true,
          canDismiss: false
        };
      case 'GRACE_LIMITED':
        return {
          className: 'evb-limited',
          icon: Clock,
          iconColor: '#f59e0b',
          message: (
            <>
              <strong>Action required:</strong> Verify your email within {hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''} to maintain full access.
            </>
          ),
          urgent: true,
          canDismiss: true
        };
      case 'GRACE_FULL':
      default:
        return {
          className: 'evb-reminder',
          icon: Mail,
          iconColor: '#0d7463',
          message: (
            <>
              Please verify your email address <strong>{email}</strong> to secure your account.
            </>
          ),
          urgent: false,
          canDismiss: true
        };
    }
  };

  const config = getBannerConfig();
  const IconComponent = config.icon;

  return (
    <div className={`email-verification-banner ${config.className}`}>
      <div className="evb-content">
        <div className="evb-info">
          <IconComponent size={18} className="evb-icon" style={{ color: config.iconColor }} />
          <span>{config.message}</span>
        </div>
        <div className="evb-actions">
          {sent ? (
            <span className="evb-sent">
              <CheckCircle size={14} /> Email sent!
            </span>
          ) : (
            <button
              className="evb-resend"
              onClick={handleResendVerification}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 size={14} className="evb-spinner" /> Sending...
                </>
              ) : (
                <>
                  Resend Email <ArrowRight size={14} />
                </>
              )}
            </button>
          )}
          {config.canDismiss && (
            <button
              className="evb-dismiss"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
